/**
 * The Tax Invoice cum Acknowledgement receipt, cut out of the acknowledgement
 * PDF uploaded at login, for the first page of the generated PAN form.
 *
 * The Protean acknowledgement is one A4 page carrying the SAME receipt twice
 * ("Copy to be kept with application" on top, a dashed cut line, then a second
 * copy). Only the top copy is wanted, so page 1 is rendered and cropped to the
 * band that copy occupies.
 *
 * The band is found from the page's own text — from "Copy to be kept with
 * application" down to "Online PAAM" — rather than hard-coded, because older
 * acknowledgements (Form 49A, 2023) are laid out differently. When the text is
 * not found the top 370pt of the page is used, which is where the first copy
 * sits on every layout seen so far.
 */

/** Render scale: ~216 DPI, enough for the barcode to stay scannable in print. */
const RENDER_SCALE = 3;
/** Space kept above and below the detected band, in PDF points. */
const BAND_MARGIN_PT = 10;
/** Band used when the marker text is not found, in PDF points from the top. */
const FALLBACK_BAND_PT: [number, number] = [0, 370];

const TOP_MARKERS = ["copy to be kept with application", "tax invoice cum acknowledgement receipt"];
const BOTTOM_MARKERS = ["online paam", "computer generated receipt"];

const blobToDataUrl = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

const isPdf = (url: string, blob: Blob) =>
  blob.type === "application/pdf" || /\.pdf(\?|#|$)/i.test(url);

/**
 * Returns the receipt as a PNG data-URL, or null when there is no file.
 * Throws when the file exists but cannot be fetched or rendered.
 */
export const extractReceiptImage = async (url: string): Promise<string | null> => {
  if (!url) return null;

  const response = await fetch(url, { method: "GET" });
  if (!response.ok) throw new Error("Could not fetch the acknowledgement PDF.");
  const blob = await response.blob();

  // An acknowledgement uploaded as a photo is already just the receipt.
  if (!isPdf(url, blob)) {
    return blob.type.startsWith("image/") ? blobToDataUrl(blob) : null;
  }

  // The webpack entry wires up the worker; it ships no typings of its own.
  // @ts-ignore
  const pdfjs: typeof import("pdfjs-dist") = await import("pdfjs-dist/webpack");

  const pdf = await pdfjs.getDocument({ data: await blob.arrayBuffer() }).promise;
  try {
    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: RENDER_SCALE });

    // Marker lines, as [top, bottom] in canvas pixels, top of page first.
    const content = await page.getTextContent();
    const lines = (content.items as any[])
      .filter((item) => typeof item?.str === "string" && item.str.trim())
      .map((item) => {
        const [, baseline] = viewport.convertToViewportPoint(
          item.transform[4],
          item.transform[5]
        );
        const height = (item.height || 10) * RENDER_SCALE;
        return {
          text: item.str.toLowerCase(),
          top: baseline - height,
          bottom: baseline,
        };
      })
      .sort((a, b) => a.top - b.top);

    const margin = BAND_MARGIN_PT * RENDER_SCALE;
    const topLine = lines.find((l) => TOP_MARKERS.some((m) => l.text.includes(m)));
    const bottomLine = topLine
      ? lines.find(
          (l) =>
            l.top > topLine.bottom &&
            BOTTOM_MARKERS.some((m) => l.text.includes(m))
        )
      : undefined;

    let bandTop: number;
    let bandBottom: number;
    if (topLine && bottomLine) {
      bandTop = Math.max(0, topLine.top - margin);
      bandBottom = Math.min(viewport.height, bottomLine.bottom + margin);
    } else {
      bandTop = FALLBACK_BAND_PT[0] * RENDER_SCALE;
      bandBottom = Math.min(viewport.height, FALLBACK_BAND_PT[1] * RENDER_SCALE);
    }

    const full = document.createElement("canvas");
    full.width = Math.ceil(viewport.width);
    full.height = Math.ceil(viewport.height);
    const fullCtx = full.getContext("2d");
    if (!fullCtx) throw new Error("Canvas is not available.");
    // White first: the PDF has no background of its own and would crop to
    // transparent, which prints fine but previews as a checkerboard.
    fullCtx.fillStyle = "#fff";
    fullCtx.fillRect(0, 0, full.width, full.height);
    await page.render({ canvasContext: fullCtx, viewport }).promise;

    const band = document.createElement("canvas");
    band.width = full.width;
    band.height = Math.ceil(bandBottom - bandTop);
    const bandCtx = band.getContext("2d");
    if (!bandCtx) throw new Error("Canvas is not available.");
    bandCtx.drawImage(
      full,
      0, bandTop, full.width, band.height,
      0, 0, band.width, band.height
    );
    return band.toDataURL("image/png");
  } finally {
    pdf.destroy();
  }
};
